import { useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { CrmSidebar } from "./CrmSidebar";
import { MobileTopBar } from "./MobileTopBar";
import { VoicePanel, VoicePanelToggle } from "@/components/voice/VoicePanel";
import { useUserSettings } from "@/hooks/useUserSettings";
import { getTenantIdentity } from "@/lib/tenant";
import { myjarvisLogoUrl } from "./myjarvis-logo";
import {
  AgentPanel,
  AgentPanelToggle,
} from "@/components/agent-panel/AgentPanel";

// Per-route background colors. The page wrapper bleeds past Layout's padding
// via negative margin to fill the column, but when the voice panel toggles
// open/closed, <main> resizes and any uncovered area shows main's own bg.
// To prevent a white surround on non-default-bg pages, we paint <main>
// with the same color the page itself uses.
const SKY_BLUE_BG = "#F2F7FD";

const SKY_BLUE_ROUTES = [
  "/meetings",
  "/flameking-manager",
  "/agents",
  "/knowledge-board",
  "/skills",
  "/projects",
  "/projects-list",
  "/goals",
  "/goals-list",
  "/kb-doc",
  "/knowledge-base",
  "/knowledge-feed",
  "/content-swipe-file",
  "/content-plan",
  "/content-map",
];

const FULL_WIDTH_ROUTES = [
  "/meetings",
  "/agents",
  "/board",
  "/marketing-board",
  "/marketing-research",
  "/design-system-board",
  "/youtube-board",
  "/knowledge-board",
  "/max-security-board",
  "/startup-board",
  "/command",
];

const NARROW_ROUTES = [
  "/kb-doc",
];

function matchesRoute(pathname: string, routes: string[]): boolean {
  return routes.some((r) => pathname === r || pathname.startsWith(r + "/"));
}

function backgroundForRoute(pathname: string): string | null {
  if (matchesRoute(pathname, SKY_BLUE_ROUTES)) return SKY_BLUE_BG;
  return null;
}

function isFullWidthRoute(pathname: string): boolean {
  return matchesRoute(pathname, FULL_WIDTH_ROUTES);
}

function isNarrowRoute(pathname: string): boolean {
  return matchesRoute(pathname, NARROW_ROUTES);
}

// MJOS-147 — the right-side slot is now mutex: voice OR agent, never both.
// Total fixed width on the right at any time = max(voice 400, agent 420) = 420.
// Plus sidebar 224 + main flex-1. Sidebar stays visible at every viewport.
type RightPanel = "voice" | "agent" | null;

export const Layout = ({ children }: { children: ReactNode }) => {
  const { settings, update } = useUserSettings();
  // Panel state is PERSISTED (Neon user_settings blob, cached in localStorage
  // for flicker-free first paint). Init from the saved value; fall back to the
  // historical voice_autoplay default only until the user first acts.
  const [rightPanel, setRightPanel] = useState<RightPanel>(() =>
    settings.right_panel !== undefined
      ? settings.right_panel
      : settings.voice_autoplay !== false
        ? "voice"
        : null,
  );
  // Left sidebar collapse — desktop only. The blue logo in the sidebar
  // collapses it; the floating logo circle at the top-left reopens it.
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(
    () => settings.sidebar_open !== false,
  );
  // Wrapped setters: update local state AND persist the choice so a refresh
  // restores it. update() is optimistic (writes cache immediately).
  const changeRightPanel = (p: RightPanel) => {
    setRightPanel(p);
    void update({ right_panel: p });
  };
  const changeSidebar = (open: boolean) => {
    setSidebarOpen(open);
    void update({ sidebar_open: open });
  };
  const { displayName: tenantDisplayName } = getTenantIdentity();

  const location = useLocation();
  const customBg = backgroundForRoute(location.pathname);
  const fullWidth = isFullWidthRoute(location.pathname);
  const narrow = isNarrowRoute(location.pathname);

  let contentClass: string;
  if (fullWidth) {
    contentClass = "flex-1";
  } else if (narrow) {
    contentClass = "mx-auto w-full max-w-[1000px] flex-1 px-4 py-8 sm:px-8 sm:py-10 md:px-12";
  } else {
    contentClass = "mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6 sm:py-8";
  }

  return (
    // overflow-x-hidden on the root flex container is a defensive measure
    // so any future overflow is clipped rather than producing a horizontal
    // scrollbar that could push the sidebar off-screen.
    <div className="flex h-svh overflow-x-hidden">
      {sidebarOpen && <CrmSidebar onCollapse={() => changeSidebar(false)} />}
      <main
        className={`relative flex min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto ${customBg ? "" : "bg-background"}`}
        style={customBg ? { background: customBg } : undefined}
      >
        <MobileTopBar />
        {/* Floating reopen button — desktop only, shown when the sidebar
            is collapsed. Mirrors the right-side panel toggle pattern. */}
        {!sidebarOpen && (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-30 hidden px-4 py-4 md:flex">
            <button
              type="button"
              onClick={() => changeSidebar(true)}
              aria-label="Open sidebar"
              title="Open sidebar"
              className="pointer-events-auto flex h-9 w-9 items-center justify-center rounded-lg transition-opacity hover:opacity-80"
            >
              <img
                src={myjarvisLogoUrl}
                alt={`${tenantDisplayName} logo`}
                className="h-9 w-9 rounded-lg object-cover"
              />
            </button>
          </div>
        )}
        {/* Top-right toggle cluster — desktop only. Whichever panel is
            currently active hides its own toggle (no point clicking it). */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-30 hidden justify-end px-6 py-4 md:flex">
          <div className="pointer-events-auto flex items-center gap-2">
            {rightPanel !== "voice" && (
              <VoicePanelToggle onClick={() => changeRightPanel("voice")} />
            )}
            {rightPanel !== "agent" && (
              <AgentPanelToggle onClick={() => changeRightPanel("agent")} />
            )}
          </div>
        </div>
        <div className={contentClass}>{children}</div>
      </main>
      <div className="hidden md:contents">
        <VoicePanel
          open={rightPanel === "voice"}
          onClose={() => changeRightPanel(null)}
        />
      </div>
      <div className="hidden md:contents">
        <AgentPanel
          open={rightPanel === "agent"}
          onClose={() => changeRightPanel(null)}
        />
      </div>
    </div>
  );
};
